import { Button } from "@/components/ui/button";
import type { SincronizacionDeTerreno } from "@/lib/terreno";
import { cn } from "@/lib/utils";

import {
  cuantasSincronizacionesNuevas,
  etiquetaDeSincronizacion,
  textoDeEstadoDeSincronizaciones,
} from "./sincronizaciones";

interface FiltroDeSincronizacionesProps {
  sincronizaciones: SincronizacionDeTerreno[];
  /** El id elegido, o `null` en "Todas". */
  elegida: string | null;
  onElegir: (id: string | null) => void;
  onMarcarRevisado: () => void;
  marcando: boolean;
}

/** La marca "NUEVA": mismo rótulo en los chips y en el popover de las demás pestañas. */
export function EtiquetaNueva() {
  return (
    <span className="bg-sync-tag text-sync-tag-foreground rounded-[4px] px-[5px] py-px text-[0.5625rem] font-extrabold tracking-[0.04em]">
      NUEVA
    </span>
  );
}

/**
 * Filtro de la pestaña "Registro de terreno": UNA sincronización a la vez (o
 * "Todas"). Filtra —oculta lo que llegó en otra—; las demás pestañas solo
 * resaltan. Los conteos son los del servidor y NO cambian con el tipo elegido.
 */
export function FiltroDeSincronizaciones({
  sincronizaciones,
  elegida,
  onElegir,
  onMarcarRevisado,
  marcando,
}: FiltroDeSincronizacionesProps) {
  if (sincronizaciones.length === 0) return null;

  const hayNuevas = cuantasSincronizacionesNuevas(sincronizaciones) > 0;

  return (
    <div className="border-divisor flex flex-wrap items-center gap-2.5 border-t pt-3">
      <span className="text-ink-muted text-[0.625rem] font-extrabold tracking-[0.07em] uppercase">
        Sincronización
      </span>

      <div role="group" aria-label="Filtrar por sincronización" className="flex flex-wrap gap-2">
        <ChipDeSincronizacion activo={elegida === null} onClick={() => onElegir(null)}>
          Todas
        </ChipDeSincronizacion>
        {sincronizaciones.map((s) => (
          <ChipDeSincronizacion
            key={s.id}
            activo={elegida === s.id}
            onClick={() => onElegir(s.id)}
            conteo={s.capturas}
            nueva={s.nueva}
            mono
          >
            {etiquetaDeSincronizacion(s.en)}
          </ChipDeSincronizacion>
        ))}
      </div>

      <p className="text-ink-muted min-w-[13.75rem] flex-1 text-[0.71875rem] font-semibold text-pretty">
        {textoDeEstadoDeSincronizaciones(sincronizaciones, elegida)}
      </p>

      {hayNuevas && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={marcando}
          onClick={onMarcarRevisado}
        >
          Marcar como revisado
        </Button>
      )}
    </div>
  );
}

function ChipDeSincronizacion({
  activo,
  onClick,
  conteo,
  nueva,
  mono,
  children,
}: {
  activo: boolean;
  onClick: () => void;
  conteo?: number;
  nueva?: boolean;
  mono?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={activo}
      onClick={onClick}
      className={cn(
        "focus-visible:ring-ring/50 inline-flex h-8 items-center gap-2 rounded-[10px] border-[1.5px] px-[11px] text-xs outline-none focus-visible:ring-[3px]",
        activo ? "bg-ink border-ink text-card font-bold" : "bg-card border-border text-ink font-semibold",
        mono && "font-mono",
      )}
    >
      {children}
      {conteo !== undefined && (
        <span className={cn("font-mono text-[0.65625rem] font-bold", activo ? "opacity-80" : "text-ink-hint")}>
          {conteo}
        </span>
      )}
      {nueva && <EtiquetaNueva />}
    </button>
  );
}
